import { Banner, Button, Select, Table, Text } from '@cloudflare/kumo'
import { DownloadSimple, LinkSimple, UploadSimple } from '@phosphor-icons/react'
import {
  FACTORY_BULK_INVITE_MAX,
  FACTORY_PROCESS_LABELS,
  csvDocument,
  csvLine,
  factoryReplyToCsv,
  parseFactoryReplyCsv,
  type AdminFactoryReplyView,
  type FactoryReply,
  type FactoryReplyBulkInviteResult,
  type FactoryView,
} from '@repo/shared'
import { useState } from 'react'
import { factoriesApi } from '../factories-api'
import { useGetToken } from '../lib/auth'
import { downloadCsv } from '../lib/download'
import { fmtTime, toMessage } from '../lib/format'

/**
 * 공장 회신 받기 (로드맵 8번). 공장 하나의 설비·공정 요약, 초대 링크 만들기, CSV 양식 내려받기·가져오기(미리보기 → 반영).
 * 관리자 설정 factoryReplies 와 서버 코드 스위치가 둘 다 켜졌을 때만 공장 목록 화면이 그린다
 */

/** 회신·공장 값 한 줄 요약 (미리보기·검토 대기 목록이 같이 쓴다) */
export function replySummary(reply: FactoryReply): string {
  const parts = [
    `설비 ${reply.equipment.length}`,
    `공정 ${reply.processes.map((p) => FACTORY_PROCESS_LABELS.ko[p]).join('·') || '없음'}`,
    `공정 단가 ${reply.processPrices.length}`,
    `최소 주문량 ${reply.moqs.length}`,
    reply.prototypeLeadTimeDays === null ? null : `시제품 ${reply.prototypeLeadTimeDays}일`,
    reply.productionLeadTimeDays === null ? null : `생산 ${reply.productionLeadTimeDays}일`,
    reply.smallBatchTrial === null
      ? null
      : `소량 시제품 ${reply.smallBatchTrial ? '가능' : '불가'}`,
    reply.certifications.length ? `인증 ${reply.certifications.join(', ')}` : null,
  ]
  return parts.filter(Boolean).join(' · ')
}

/** 공장 목록 값 → 회신 꼴 (CSV 내려받기에 지금 값을 채우려고). 받은 적 없으면 null */
const currentReply = (f: FactoryView): FactoryReply | null =>
  f.capabilities ? { ...f.capabilities, certifications: f.certifications } : null

export function FactoryIntakePanel({
  factory,
  onChanged,
}: {
  factory: FactoryView
  onChanged: () => Promise<void>
}) {
  const getToken = useGetToken()
  const [lang, setLang] = useState<'ko' | 'en'>('ko')
  const [invite, setInvite] = useState<string | null>(null)
  const [preview, setPreview] = useState<FactoryReply | null>(null)
  const [errors, setErrors] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setNotice(null)
    try {
      await fn()
    } catch (err) {
      setErrors([toMessage(err)])
    } finally {
      setBusy(false)
    }
  }
  const current = currentReply(factory)

  return (
    <div
      className="border-kumo-line flex flex-col gap-4 rounded-lg border p-5"
      data-testid="factory-intake"
    >
      <Text variant="heading">{factory.name} · 설비·공정 회신</Text>
      <Text variant="secondary" size="sm">
        지금 값: {current ? replySummary(current) : '아직 받은 회신이 없어요.'}
      </Text>

      <div className="flex flex-wrap items-end gap-3">
        <Select
          label="초대 링크 언어"
          value={lang}
          onValueChange={(v: string | null) => v && setLang(v as 'ko' | 'en')}
          items={[
            { label: '한국어', value: 'ko' },
            { label: 'English', value: 'en' },
          ]}
        />
        <Button
          variant="secondary"
          icon={<LinkSimple />}
          loading={busy}
          onClick={() =>
            void run(async () => {
              const created = await factoriesApi.createInvite(getToken, factory.id, lang)
              setInvite(`${created.url} (${created.expiresAt.slice(0, 10)}까지)`)
            })
          }
        >
          초대 링크 만들기
        </Button>
        <Button
          variant="secondary"
          icon={<DownloadSimple />}
          onClick={() => downloadCsv(`factory-${factory.name}.csv`, factoryReplyToCsv(current))}
        >
          CSV 양식 내려받기
        </Button>
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <UploadSimple />
          CSV 가져오기
          <input
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              e.target.value = ''
              if (!file) return
              void file.text().then((text) => {
                const parsed = parseFactoryReplyCsv(text)
                setNotice(null)
                setErrors(parsed.ok ? [] : parsed.errors)
                setPreview(parsed.ok ? parsed.reply : null)
              })
            }}
          />
        </label>
      </div>
      {invite && (
        <Banner
          variant="default"
          title="초대 링크 (지금 한 번만 보여요. 공장에 보낼 메일에 붙이세요)"
          description={invite}
        />
      )}
      {errors.length > 0 && (
        <Banner variant="error" title="반영할 수 없어요" description={errors.join(' / ')} />
      )}
      {preview && (
        <div className="flex flex-col gap-2" data-testid="factory-import-preview">
          <Text size="sm">가져올 값: {replySummary(preview)}</Text>
          <Text variant="secondary" size="sm">
            반영하면 이 공장의 설비·공정·단가·최소 주문량과 인증이 위 값으로 바뀌어요.
          </Text>
          <div className="flex gap-2">
            <Button
              variant="primary"
              loading={busy}
              onClick={() =>
                void run(async () => {
                  await factoriesApi.importCapabilities(getToken, factory.id, preview)
                  setPreview(null)
                  setNotice('반영했어요.')
                  await onChanged()
                })
              }
            >
              반영
            </Button>
            <Button variant="ghost" disabled={busy} onClick={() => setPreview(null)}>
              취소
            </Button>
          </div>
        </div>
      )}
      {notice && <Banner variant="default" title={notice} />}
    </div>
  )
}

/** 공장이 웹 폼으로 보낸 회신 중 검토 대기. 반영해야 공장 목록에 들어간다 */
export function PendingReplies({
  items,
  onChanged,
}: {
  items: AdminFactoryReplyView[]
  onChanged: () => Promise<void>
}) {
  const getToken = useGetToken()
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const act = (id: string, fn: () => Promise<unknown>) => {
    setBusy(id)
    setError(null)
    void fn()
      .then(onChanged)
      .catch((err: unknown) => setError(toMessage(err)))
      .finally(() => setBusy(null))
  }
  if (items.length === 0) return null
  return (
    <div className="flex flex-col gap-2" data-testid="factory-replies">
      <Text variant="heading">검토 대기 회신 {items.length}건</Text>
      {error && <Banner variant="error" title="처리하지 못했어요" description={error} />}
      <div className="overflow-x-auto">
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.Head>공장</Table.Head>
              <Table.Head>받은 시각</Table.Head>
              <Table.Head>내용</Table.Head>
              <Table.Head />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {items.map((r) => (
              <Table.Row key={r.id}>
                <Table.Cell>{r.factoryName}</Table.Cell>
                <Table.Cell>{fmtTime(r.createdAt)}</Table.Cell>
                <Table.Cell className="max-w-96 break-words">
                  {replySummary(r.reply)}
                  {r.reply.notes ? ` · 메모: ${r.reply.notes}` : ''}
                </Table.Cell>
                <Table.Cell>
                  <div className="flex gap-1">
                    <Button
                      variant="primary"
                      size="sm"
                      loading={busy === r.id}
                      onClick={() => act(r.id, () => factoriesApi.applyReply(getToken, r.id))}
                    >
                      반영
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy === r.id}
                      onClick={() => act(r.id, () => factoriesApi.rejectReply(getToken, r.id))}
                    >
                      거절
                    </Button>
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    </div>
  )
}

/**
 * 상세 확인 메일 대상마다 회신 양식 초대 링크를 만들고 CSV(공장·대표 메일·문의폼·링크)로 내려받는다 (대표 결정 10/30).
 * 메일은 보내지 않는다 — 대표 승인 뒤 이 파일로 보낸다. 중단·연락 금지 공장은 서버도 건너뛴다
 */
export function BulkInviteButton({ targets }: { targets: readonly FactoryView[] }) {
  const getToken = useGetToken()
  const [lang, setLang] = useState<'ko' | 'en'>('ko')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const make = async () => {
    setBusy(true)
    setResult(null)
    try {
      const ids = targets.map((f) => f.id)
      const items: FactoryReplyBulkInviteResult['items'] = []
      let skipped = 0
      for (let i = 0; i < ids.length; i += FACTORY_BULK_INVITE_MAX) {
        const part = await factoriesApi.bulkInvites(
          getToken,
          ids.slice(i, i + FACTORY_BULK_INVITE_MAX),
          lang,
        )
        items.push(...part.items)
        skipped += part.skipped.length
      }
      downloadCsv(
        'factory-reply-links.csv',
        csvDocument([
          csvLine(['공장', '대표 메일', '문의폼', '회신 양식 링크', '기한']),
          ...items.map((x) =>
            csvLine([x.name, x.email, x.contactFormUrl, x.url, x.expiresAt.slice(0, 10)]),
          ),
        ]),
      )
      setResult(`링크 ${items.length}개를 만들어 내려받았어요. 건너뜀 ${skipped}곳.`)
    } catch (err) {
      setResult(toMessage(err))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="factory-bulk-invite">
      <Select
        label="양식 언어"
        value={lang}
        onValueChange={(v: string | null) => v && setLang(v as 'ko' | 'en')}
        items={[
          { label: '한국어', value: 'ko' },
          { label: 'English', value: 'en' },
        ]}
      />
      <Button
        variant="secondary"
        icon={<LinkSimple />}
        loading={busy}
        disabled={targets.length === 0}
        onClick={() => void make()}
      >
        {targets.length}곳 회신 양식 링크 만들기
      </Button>
      {result && (
        <Text variant="secondary" size="sm">
          {result}
        </Text>
      )}
    </div>
  )
}
